import { useState } from "react";
import { ArrowLeftRight, Check, Printer } from "lucide-react";
import { verfahren as verfahrenDe, verfahrenEn, verfahrenFr, verfahrenIt, verfahrenList } from "@/lib/verfahren-data";
import { useT, useI18n } from "@/lib/i18n";
import { verfahrenLabels } from "@/lib/translations";

const FIELD_LABELS = {
  de: [
    ["schichtdicke", "Typische Schichtdicke"],
    ["korrosionsschutz", "Korrosionsschutz"],
    ["materialien", "Geeignete Werkstoffe"],
    ["optik", "Optik"],
    ["serientauglich", "Serientauglich"],
    ["anwendungen", "Typische Anwendungen"],
  ],
  en: [
    ["schichtdicke", "Typical thickness"],
    ["korrosionsschutz", "Corrosion protection"],
    ["materialien", "Suitable materials"],
    ["optik", "Appearance"],
    ["serientauglich", "Series-capable"],
    ["anwendungen", "Typical applications"],
  ],
  fr: [
    ["schichtdicke", "Épaisseur typique"],
    ["korrosionsschutz", "Protection anti-corrosion"],
    ["materialien", "Matériaux compatibles"],
    ["optik", "Aspect"],
    ["serientauglich", "Aptitude série"],
    ["anwendungen", "Applications typiques"],
  ],
  it: [
    ["schichtdicke", "Spessore tipico"],
    ["korrosionsschutz", "Protezione anticorrosiva"],
    ["materialien", "Materiali idonei"],
    ["optik", "Aspetto"],
    ["serientauglich", "Idoneità in serie"],
    ["anwendungen", "Applicazioni tipiche"],
  ],
};

const COPY = {
  de: {
    intro: "Schichtdicke, Korrosionsschutz, Optik, Serientauglichkeit — alle Kennwerte direkt nebeneinander. Für die schnelle, fundierte Entscheidung im Engineering.",
    a_label: "Verfahren A", b_label: "Verfahren B",
    criterion: "Kriterium",
    note_disclaimer: "Erfahrungswerte — verbindliche Spezifikation in der Offerte.",
    save_pdf: "Vergleich als PDF speichern",
    pdf_title: "Verfahren-Vergleich",
  },
  en: {
    intro: "Coating thickness, corrosion protection, appearance, series capability — all key values side by side. For fast, well-founded engineering decisions.",
    a_label: "Process A", b_label: "Process B",
    criterion: "Criterion",
    note_disclaimer: "Experience values — binding specification in the quote.",
    save_pdf: "Save comparison as PDF",
    pdf_title: "Process comparison",
  },
  fr: {
    intro: "Épaisseur de couche, protection anti-corrosion, aspect, aptitude série — toutes les caractéristiques clés côte à côte. Pour une décision d'ingénierie rapide et solide.",
    a_label: "Procédé A", b_label: "Procédé B",
    criterion: "Critère",
    note_disclaimer: "Valeurs d'expérience — la spécification ferme figure dans l'offre.",
    save_pdf: "Enregistrer le comparatif en PDF",
    pdf_title: "Comparatif des procédés",
  },
  it: {
    intro: "Spessore, protezione anticorrosiva, aspetto, idoneità in serie — tutti i parametri chiave a confronto diretto. Per decisioni di engineering rapide e solide.",
    a_label: "Procedimento A", b_label: "Procedimento B",
    criterion: "Criterio",
    note_disclaimer: "Valori d'esperienza — la specifica vincolante è nell'offerta.",
    save_pdf: "Salva confronto in PDF",
    pdf_title: "Confronto procedimenti",
  },
};

export const VerfahrenCompare = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const fields = FIELD_LABELS[lang] || FIELD_LABELS.de;
  const dataset = lang === "en" ? verfahrenEn : lang === "fr" ? verfahrenFr : lang === "it" ? verfahrenIt : verfahrenDe;
  const labelMap = verfahrenLabels[lang] || verfahrenLabels.de;
  const trVerfahren = (v) => labelMap[v] || v;

  const [a, setA] = useState("Verzinken");
  const [b, setB] = useState("Zink-Nickel");
  const dataA = dataset[a];
  const dataB = dataset[b];

  const renderValue = (v) =>
    Array.isArray(v) ? (
      <div className="flex flex-wrap gap-1.5">
        {v.map((x) => (
          <span key={x} className="inline-block text-[12px] px-2 py-0.5 border border-white/10 text-zinc-300">{x}</span>
        ))}
      </div>
    ) : (<div className="text-sm text-zinc-200">{v}</div>);

  const printPDF = () => {
    const escape = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const date = new Date().toLocaleDateString(lang === "en" ? "en-GB" : lang === "fr" ? "fr-CH" : lang === "it" ? "it-CH" : "de-CH", { day: "2-digit", month: "long", year: "numeric" });
    const cell = (v) => Array.isArray(v) ? v.map(escape).join(", ") : escape(v);
    const rows = fields.map(([k, label]) => `<tr><td class="lbl">${escape(label)}</td><td>${cell(dataA[k])}</td><td>${cell(dataB[k])}</td></tr>`).join("");
    const aLabel = trVerfahren(a);
    const bLabel = trVerfahren(b);

    const html = `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8"><title>${c.pdf_title} · ${escape(aLabel)} vs ${escape(bLabel)} · Fritz Kamm AG</title>
<style>
  @page { size: A4 landscape; margin: 16mm; } * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; margin: 0; }
  .head { display:flex; justify-content:space-between; border-bottom:2px solid #111; padding-bottom:12px; margin-bottom:24px; }
  .brand { display:flex; align-items:center; gap:12px; }
  .brand-mark { width:38px; height:38px; border:1.5px solid #111; display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;letter-spacing:-.02em; }
  .brand-name { font-weight:700; font-size:14px; letter-spacing:.04em; }
  .brand-sub { font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#666; margin-top:2px; }
  .meta { font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:#666; text-align:right; line-height:1.6; }
  .overline { font-size:10px; letter-spacing:.22em; text-transform:uppercase; color:#666; margin-bottom:6px; }
  h1 { font-size:30px; line-height:1.05; letter-spacing:-.02em; margin:0 0 8px; font-weight:700; }
  .titles { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:18px; }
  .title-card { border:1px solid #ddd; padding:14px 16px; }
  .title-card h2 { font-size:22px; margin:0 0 4px; font-weight:700; letter-spacing:-.02em; }
  .title-card p { font-size:12px; color:#555; margin:0; line-height:1.55; }
  table.cmp { width:100%; border-collapse:collapse; }
  table.cmp td { padding:11px 12px; font-size:12px; border:1px solid #e2e2e2; vertical-align:top; }
  table.cmp td.lbl { width:28%; color:#666; background:#fafafa; text-transform:uppercase; letter-spacing:.14em; font-size:10px; }
  table.cmp tr td:nth-child(2), table.cmp tr td:nth-child(3) { width:36%; }
  .foot { margin-top:28px; border-top:1px solid #ddd; padding-top:14px; display:flex; justify-content:space-between; gap:16px; font-size:11px; color:#555; line-height:1.6; }
  .foot strong { color:#111; }
</style></head><body>
  <div class="head">
    <div class="brand">
      <div class="brand-mark">FK</div>
      <div>
        <div class="brand-name">FRITZ KAMM AG</div>
        <div class="brand-sub">${lang === "en" ? "Electroplating · Mels · Switzerland" : lang === "fr" ? "Galvanoplastie · Mels · Suisse" : lang === "it" ? "Galvanica · Mels · Svizzera" : "Galvanik · Mels · Schweiz"}</div>
      </div>
    </div>
    <div class="meta">${escape(c.pdf_title)}<br>${escape(date)}<br>ISO 9001 · ISO 14001</div>
  </div>

  <div class="overline">${lang === "en" ? "Comparison" : lang === "fr" ? "Comparatif" : lang === "it" ? "Confronto" : "Vergleich"}</div>
  <h1>${escape(aLabel)} <span style="color:#999;font-weight:400;">vs.</span> ${escape(bLabel)}</h1>

  <div class="titles">
    <div class="title-card"><h2>${escape(aLabel)}</h2><p>${escape(dataA.note)}</p></div>
    <div class="title-card"><h2>${escape(bLabel)}</h2><p>${escape(dataB.note)}</p></div>
  </div>

  <table class="cmp">
    <thead><tr><td class="lbl">${escape(c.criterion)}</td><td><strong>${escape(aLabel)}</strong></td><td><strong>${escape(bLabel)}</strong></td></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="foot">
    <div><strong>Fritz Kamm AG</strong><br>Zeughausstrasse 12<br>CH-8887 Mels</div>
    <div><strong>${lang === "en" ? "Contact" : lang === "fr" ? "Contact" : lang === "it" ? "Contatti" : "Kontakt"}</strong><br>+41 81 723 16 42<br>sekretariat@f-kamm.ch</div>
    <div style="text-align:right;max-width:280px;">${lang === "en" ? "All key values are experience values. The binding specification is prepared based on your part requirements in the quote." : lang === "fr" ? "Toutes les caractéristiques sont des valeurs d'expérience. La spécification ferme est établie sur la base de vos exigences dans l'offre." : lang === "it" ? "Tutti i parametri sono valori d'esperienza. La specifica vincolante viene preparata in offerta sulla base dei requisiti del componente." : "Alle Kennwerte sind Erfahrungswerte. Die verbindliche Spezifikation erstellen wir auf Basis Ihrer Bauteilanforderungen in der Offerte."}</div>
  </div>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open(); w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 350);
  };

  return (
    <section
      id="vergleich"
      data-testid="compare-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <ArrowLeftRight className="w-4 h-4" strokeWidth={1.5} />
              <span>{t("sections.vergleich_overline", "— Verfahren-Vergleich")}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.vergleich_title_1", "Zwei Verfahren.")}
              <br />
              <span className="text-zinc-500">{t("sections.vergleich_title_2", "Eine klare Entscheidung.")}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-px">
          <div className="bg-[#0E0E10] p-6 md:p-8">
            <div className="overline mb-3">{c.a_label}</div>
            <select
              data-testid="compare-select-a"
              value={a}
              onChange={(e) => setA(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-white/15 text-white font-display text-2xl md:text-3xl tracking-tight py-3 cursor-pointer focus:outline-none focus:border-white"
            >
              {verfahrenList.map((v) => (
                <option key={v} value={v} className="bg-[#0E0E10] font-body text-base">{trVerfahren(v)}</option>
              ))}
            </select>
            <div className="mt-3 text-sm text-zinc-400 leading-relaxed">{dataA.note}</div>
          </div>
          <div className="bg-[#0E0E10] p-6 md:p-8">
            <div className="overline mb-3">{c.b_label}</div>
            <select
              data-testid="compare-select-b"
              value={b}
              onChange={(e) => setB(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-white/15 text-white font-display text-2xl md:text-3xl tracking-tight py-3 cursor-pointer focus:outline-none focus:border-white"
            >
              {verfahrenList.map((v) => (
                <option key={v} value={v} className="bg-[#0E0E10] font-body text-base">{trVerfahren(v)}</option>
              ))}
            </select>
            <div className="mt-3 text-sm text-zinc-400 leading-relaxed">{dataB.note}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
          <div className="bg-[#0E0E10] p-4 md:p-5 overline text-zinc-500">{c.criterion}</div>
          <div className="bg-[#0E0E10] p-4 md:p-5 overline text-white">{trVerfahren(a)}</div>
          <div className="bg-[#0E0E10] p-4 md:p-5 overline text-white">{trVerfahren(b)}</div>

          {fields.map(([key, label]) => (
            <div key={key} className="contents">
              <div className="bg-[#0B0B0D] p-4 md:p-5 text-[13px] text-zinc-300 font-medium">{label}</div>
              <div data-testid={`compare-row-${key}-a`} className="bg-[#0E0E10] p-4 md:p-5">{renderValue(dataA[key])}</div>
              <div data-testid={`compare-row-${key}-b`} className="bg-[#0E0E10] p-4 md:p-5">{renderValue(dataB[key])}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 text-[12px] text-zinc-500">
            <Check className="w-4 h-4" strokeWidth={1.5} />
            {c.note_disclaimer}
          </div>
          <button
            type="button"
            onClick={printPDF}
            data-testid="compare-print-pdf"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white border border-white/15 hover:border-white/40 px-4 py-2.5 transition-colors"
          >
            <Printer className="w-4 h-4" strokeWidth={1.5} />
            {c.save_pdf}
          </button>
        </div>
      </div>
    </section>
  );
};

export default VerfahrenCompare;
